import { useTranslation } from "react-i18next";
import { EyeOff } from "lucide-react";

import type { ActId } from "@/components/projects/diagnostic/acts";
import { PRESENTER_NOTES } from "@/components/projects/diagnostic/presenterNotesData";

/**
 * O bloco de notas do apresentador ao final de cada ato (#852).
 *
 * Fica visualmente **fora** da linguagem dos cards: fundo escuro, tipografia
 * menor, ícone de olho riscado. É proposital. Quem apresenta precisa reconhecer
 * num relance o que é para o cliente ler e o que é para ele; se as notas
 * parecessem um card, alguém leria em voz alta o que não devia.
 */
export function PresenterNotes({ act }: { act: ActId }) {
  const { t } = useTranslation();
  const notes = PRESENTER_NOTES[act];

  if (!notes?.length) return null;

  return (
    <aside
      data-testid="diagnostic-presenter-notes"
      className="mt-8 rounded-lg border border-dashed border-[#e3c271] bg-[#fffcf2] px-6 py-5 text-[#08254c]"
    >
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.1em] text-[#a07b1e]">
        <EyeOff aria-hidden className="h-3.5 w-3.5" />
        {t("Presenter notes")} · {t("not included in the client PDF")}
      </div>

      <dl className="mt-4 grid gap-5 sm:grid-cols-2">
        {notes.map((note) => (
          <div key={note.title} className="flex flex-col gap-1.5">
            <dt className="font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
              {t(note.title)}
            </dt>
            <dd className="m-0 text-[13px] leading-relaxed text-[#3d4f66]">
              {t(note.body)}
            </dd>
          </div>
        ))}
      </dl>
    </aside>
  );
}
